/**
 * Custom FDI Map Notification System
 * Enhanced visual loading states without external dependencies
 */

import { addClass, removeClass } from "./dom-helpers.js";

/**
 * Configuration for the notification system
 */
const NOTIFICATION_CONFIG = {
  position: "top-right", // top-right, top-left, bottom-right, bottom-left, center
  maxVisible: 3,
  defaultDuration: 3000,
  animations: {
    slideIn: "slide-in-right",
    slideOut: "slide-out-right",
    fadeIn: "fade-in",
    fadeOut: "fade-out",
  },
  zIndex: 10000,
  // Enhanced UX settings
  mobile: {
    position: "top-center",
    maxWidth: "90vw",
    fontSize: "14px",
    padding: "12px 16px",
  },
  desktop: {
    position: "top-right",
    maxWidth: "400px",
    fontSize: "16px",
    padding: "16px 20px",
  },
};

/**
 * Notification manager to handle multiple notifications
 */
class NotificationManager {
  constructor() {
    this.activeNotifications = new Set();
    this.container = null;
    this.initializeContainer();
  }

  initializeContainer() {
    // Create notification container if it doesn't exist
    this.container = document.getElementById("cwp-notification-container");
    if (!this.container) {
      this.container = document.createElement("div");
      this.container.id = "cwp-notification-container";

      // Responsive positioning
      const isMobile = window.innerWidth <= 768;
      const position = isMobile
        ? NOTIFICATION_CONFIG.mobile.position
        : NOTIFICATION_CONFIG.desktop.position;

      this.container.className = `cwp-notification-container cwp-position-${position}`;
      document.body.appendChild(this.container);
    }
  }

  addNotification(notification) {
    this.activeNotifications.add(notification);
    this.container.appendChild(notification);

    // Limit the number of visible notifications
    if (this.activeNotifications.size > NOTIFICATION_CONFIG.maxVisible) {
      const oldest = Array.from(this.activeNotifications)[0];
      this.removeNotification(oldest, false);
    }
  }

  removeNotification(notification, animated = true) {
    if (!this.activeNotifications.has(notification)) return;

    this.activeNotifications.delete(notification);

    if (animated) {
      removeClass(notification, "cwp-notification-show");
      addClass(notification, "cwp-notification-hide");
      setTimeout(() => {
        if (notification.parentNode) {
          notification.remove();
        }
      }, 300);
    } else {
      if (notification.parentNode) {
        notification.remove();
      }
    }
  }

  clearAll() {
    this.activeNotifications.forEach((notification) => {
      this.removeNotification(notification, false);
    });
  }
}

// Create global notification manager
const notificationManager = new NotificationManager();

/**
 * Show a standard notification
 * @param {string} message - The message to display
 * @param {boolean} isError - Whether this is an error notification
 * @param {number} duration - How long to show the notification (ms)
 * @param {Object} options - Additional options
 */
export function showNotification(
  message,
  isError = false,
  duration = NOTIFICATION_CONFIG.defaultDuration,
  options = {},
) {
  // Skip cache operation notifications unless explicitly requested
  if (message.toLowerCase().includes("cache") && !options.forceShow) {
    return null;
  }

  const notification = document.createElement("div");
  const notificationId = `notification-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  notification.id = notificationId;
  // Enhanced notification with modern design
  const isMobile = window.innerWidth <= 768;
  const notificationType = isError ? "error" : "success";

  notification.className = `cwp-notification cwp-notification-${notificationType} cwp-notification-${isMobile ? "mobile" : "desktop"}`;

  // Enhanced notification structure with modern design
  notification.innerHTML = `
    <div class="cwp-notification-content">
      <div class="cwp-notification-icon">
        ${
          isError
            ? '<svg class="cwp-icon-error" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><line x1="15" y1="9" x2="9" y2="15"></line><line x1="9" y1="9" x2="15" y2="15"></line></svg>'
            : '<svg class="cwp-icon-success" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="m9 12 2 2 4-4"></path></svg>'
        }
      </div>
      <div class="cwp-notification-message">${message}</div>
      <button class="cwp-notification-close" aria-label="Close notification" tabindex="0">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>
    <div class="cwp-notification-progress"></div>
  `;

  // Add click handler for close button
  const closeButton = notification.querySelector(".cwp-notification-close");
  closeButton.addEventListener("click", () => {
    notificationManager.removeNotification(notification);
  });

  // Add keyboard support for accessibility
  closeButton.addEventListener("keydown", (e) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      notificationManager.removeNotification(notification);
    }
  });

  // Add to manager
  notificationManager.addNotification(notification);

  // Animate in
  setTimeout(() => addClass(notification, "cwp-notification-show"), 50);

  // Auto remove after duration
  if (duration > 0) {
    const progressBar = notification.querySelector(".cwp-notification-progress");
    if (progressBar) {
      progressBar.style.animationDuration = `${duration}ms`;
      addClass(progressBar, "cwp-progress-animate");
    }

    setTimeout(() => {
      notificationManager.removeNotification(notification);
    }, duration);
  }

  return notification;
}

/**
 * Show enhanced loading notification with progress capabilities
 * @param {string} message - Loading message
 * @param {Object} options - Loading options
 */
export function showLoadingNotification(message, options = {}) {
  const {
    showProgress = false,
    showSpinner = true,
    persistent = false,
    style = "default", // default, minimal, skeleton
  } = options;

  const notification = document.createElement("div");
  const notificationId = `loading-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  notification.id = notificationId;
  // Enhanced loading notification with modern design
  const isMobile = window.innerWidth <= 768;

  notification.className = `cwp-notification cwp-notification-loading cwp-loading-${style} cwp-notification-${isMobile ? "mobile" : "desktop"}`;

  // Enhanced loading notification structure with modern design
  notification.innerHTML = `
    <div class="cwp-notification-content">
      <div class="cwp-notification-icon">
        ${
          showSpinner
            ? `
          <div class="cwp-loading-spinner">
            <div class="cwp-spinner-ring"></div>
            <div class="cwp-spinner-ring"></div>
            <div class="cwp-spinner-ring"></div>
          </div>
        `
            : ""
        }
      </div>
      <div class="cwp-notification-message">${message}</div>
      ${
        !persistent
          ? `
        <button class="cwp-notification-close" aria-label="Close loading" tabindex="0">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      `
          : ""
      }
    </div>
    ${showProgress ? '<div class="cwp-loading-progress"><div class="cwp-progress-bar"></div></div>' : ""}
  `;

  // Add click handler for close button if not persistent
  if (!persistent) {
    const closeButton = notification.querySelector(".cwp-notification-close");
    if (closeButton) {
      closeButton.addEventListener("click", () => {
        notificationManager.removeNotification(notification);
      });

      // Add keyboard support for accessibility
      closeButton.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          notificationManager.removeNotification(notification);
        }
      });
    }
  }

  // Add to manager
  notificationManager.addNotification(notification);

  // Animate in
  setTimeout(() => addClass(notification, "cwp-notification-show"), 50);

  return notification;
}

/**
 * Update loading notification message and progress
 * @param {HTMLElement} notification - The loading notification element
 * @param {string} message - New message
 * @param {number} progress - Progress percentage (0-100)
 */
export function updateLoadingNotification(notification, message, progress = null) {
  if (!notification || !notification.parentNode) return false;

  const messageElement = notification.querySelector(".cwp-notification-message");
  if (messageElement) {
    messageElement.textContent = message;
  }

  if (progress !== null) {
    const progressBar = notification.querySelector(".cwp-progress-bar");
    if (progressBar) {
      progressBar.style.width = `${Math.min(100, Math.max(0, progress))}%`;
    }
  }

  return true;
}

/**
 * Hide notification
 * @param {HTMLElement} notification - The notification to hide
 */
export function hideNotification(notification) {
  if (notification && notificationManager.activeNotifications.has(notification)) {
    notificationManager.removeNotification(notification);
  }
}

/**
 * Show progress notification for batch operations
 * @param {string} title - Title of the operation
 * @param {number} total - Total number of items
 * @param {number} current - Current progress
 * @param {string} currentItem - Name of current item being processed
 */
export function showProgressNotification(title, total, current, currentItem = "") {
  const progress = total > 0 ? Math.round((current / total) * 100) : 0;
  const message = `${title} (${current}/${total})${currentItem ? ` - ${currentItem}` : ""}`;

  // Try to find existing progress notification
  let existingNotification = document.querySelector(
    `[id^="progress-${title.replace(/\s+/g, "-")}"]`,
  );

  if (existingNotification) {
    updateLoadingNotification(existingNotification, message, progress);
    return existingNotification;
  } else {
    // Create new progress notification
    const notification = showLoadingNotification(message, {
      showProgress: true,
      persistent: true,
      style: "default",
    });

    notification.id = `progress-${title.replace(/\s+/g, "-")}-${Date.now()}`;
    updateLoadingNotification(notification, message, progress);

    return notification;
  }
}

/**
 * Clear all notifications
 */
export function clearAllNotifications() {
  notificationManager.clearAll();
}

// Export the notification manager for advanced usage
export { notificationManager };
